import React, { useState, useRef, useEffect } from 'react';
import '../styles/home.css';
import { PDFDocument, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const TOOLS = {
  SELECT: 'select',
  TEXT: 'text',
  IMAGE: 'image',
  RECT: 'rect',
  CIRCLE: 'circle',
  LINE: 'line',
  FREEHAND: 'freehand',
};

const FONTS = ['Helvetica', 'Times-Roman', 'Courier'];

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return { r, g, b };
};

const PdfAnnotator = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagePreview, setPagePreview] = useState(null);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [submitValue, setSubmitValue] = useState('Save PDF');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [resultBlob, setResultBlob] = useState(null);

  // Tool state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [fillColor, setFillColor] = useState('#FF9900');
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState('Helvetica');
  const [brushSize, setBrushSize] = useState(3);
  const [shapeStrokeWidth, setShapeStrokeWidth] = useState(2);
  const [applyMode, setApplyMode] = useState('current'); // 'current', 'all', 'even', 'odd'

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentFreehand, setCurrentFreehand] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);

  const containerRef = useRef(null);
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const pdfDocRef = useRef(null);
  const canvasRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setPdfFile(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPagePreview(null);
    setSubmitValue('Save PDF');
    setNotify('');
    setError(null);
    setStep(1);
    setResultBlob(null);
    setAnnotations([]);
    setSelectedAnnotation(null);
    setActiveTool(TOOLS.SELECT);
    setEditingTextId(null);
    setApplyMode('current');
    if (pdfInputRef.current) pdfInputRef.current.value = null;
  };

  const handlePdfChange = async (event) => {
    const file = event.target.files[0];
    if (!file || file.type !== constants.pdfFileType) {
      setNotify('Please select a valid PDF file');
      setError('error');
      return;
    }
    setPdfFile(file);
    setNotify('');
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      setStep(2);
      await renderPage(pdf, 1);
    } catch (err) {
      setNotify('Failed to load PDF. It may be encrypted.');
      setError('error');
    }
  };

  const renderPage = async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const containerWidth = 600;
    const newScale = containerWidth / viewport.width;
    setScale(newScale);
    const scaledViewport = page.getViewport({ scale: newScale * (zoom / 100) });
    setPageDimensions({ width: scaledViewport.width, height: scaledViewport.height });
    const canvas = document.createElement('canvas');
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
    setPagePreview(canvas.toDataURL());
  };

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || !pdfDocRef.current) return;
    setCurrentPage(newPage);
    setSelectedAnnotation(null);
    setEditingTextId(null);
    await renderPage(pdfDocRef.current, newPage);
  };

  const handleZoom = async (delta) => {
    const newZoom = Math.max(50, Math.min(200, zoom + delta));
    setZoom(newZoom);
    if (pdfDocRef.current) {
      const page = await pdfDocRef.current.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1 });
      const containerWidth = 600;
      const newScale = containerWidth / viewport.width;
      setScale(newScale);
      const scaledViewport = page.getViewport({ scale: newScale * (newZoom / 100) });
      setPageDimensions({ width: scaledViewport.width, height: scaledViewport.height });
      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      setPagePreview(canvas.toDataURL());
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.match(/image\/(png|jpeg|jpg|webp)/i)) {
      setNotify('Please select a PNG, JPG, or WebP image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxSize = 150;
        const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const annotation = {
          id: Date.now() + Math.random(),
          type: 'image',
          page: currentPage,
          applyMode,
          x: pageDimensions.width / 2 - (img.width * ratio) / 2,
          y: pageDimensions.height / 2 - (img.height * ratio) / 2,
          width: Math.round(img.width * ratio),
          height: Math.round(img.height * ratio),
          src: e.target.result,
          file,
          opacity: 100,
        };
        setAnnotations((prev) => [...prev, annotation]);
        setSelectedAnnotation(annotation.id);
        setActiveTool(TOOLS.SELECT);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
    if (imageInputRef.current) imageInputRef.current.value = null;
  };

  const getMousePos = (e) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleCanvasMouseDown = (e) => {
    const pos = getMousePos(e);

    if (activeTool === TOOLS.TEXT) {
      const annotation = {
        id: Date.now() + Math.random(),
        type: 'text',
        page: currentPage,
        applyMode,
        x: pos.x,
        y: pos.y,
        text: 'Type here',
        fontSize,
        fontFamily,
        color: strokeColor,
      };
      setAnnotations((prev) => [...prev, annotation]);
      setSelectedAnnotation(annotation.id);
      setEditingTextId(annotation.id);
      setActiveTool(TOOLS.SELECT);
      return;
    }

    if (activeTool === TOOLS.FREEHAND) {
      setIsDrawing(true);
      setCurrentFreehand({
        id: Date.now() + Math.random(),
        type: 'freehand',
        page: currentPage,
        applyMode,
        points: [pos],
        color: strokeColor,
        brushSize,
      });
      return;
    }

    if (activeTool === TOOLS.RECT || activeTool === TOOLS.CIRCLE || activeTool === TOOLS.LINE) {
      setIsDrawing(true);
      setDrawStart(pos);
      return;
    }

    if (activeTool === TOOLS.SELECT) {
      // Check if clicking on an annotation
      const pageAnnotations = annotations.filter((a) => a.page === currentPage);
      let clicked = null;
      for (let i = pageAnnotations.length - 1; i >= 0; i--) {
        const a = pageAnnotations[i];
        if (isPointInAnnotation(pos, a)) {
          clicked = a;
          break;
        }
      }
      if (clicked) {
        setSelectedAnnotation(clicked.id);
        setEditingTextId(null);
        setDragging({
          id: clicked.id,
          offsetX: pos.x - clicked.x,
          offsetY: pos.y - clicked.y,
        });
      } else {
        setSelectedAnnotation(null);
        setEditingTextId(null);
      }
    }
  };

  const isPointInAnnotation = (pos, a) => {
    if (a.type === 'text') {
      const approxWidth = a.text.length * a.fontSize * 0.6;
      const approxHeight = a.fontSize * 1.2;
      return pos.x >= a.x && pos.x <= a.x + approxWidth && pos.y >= a.y - approxHeight && pos.y <= a.y;
    }
    if (a.type === 'image' || a.type === 'rect') {
      return pos.x >= a.x && pos.x <= a.x + a.width && pos.y >= a.y && pos.y <= a.y + a.height;
    }
    if (a.type === 'circle') {
      const cx = a.x + a.width / 2;
      const cy = a.y + a.height / 2;
      const rx = Math.abs(a.width) / 2;
      const ry = Math.abs(a.height) / 2;
      if (rx === 0 || ry === 0) return false;
      return Math.pow(pos.x - cx, 2) / Math.pow(rx, 2) + Math.pow(pos.y - cy, 2) / Math.pow(ry, 2) <= 1;
    }
    if (a.type === 'line') {
      const dist = distToSegment(pos, { x: a.x, y: a.y }, { x: a.x2, y: a.y2 });
      return dist < 8;
    }
    if (a.type === 'freehand' && a.points && a.points.length > 0) {
      return a.points.some((p) => Math.abs(pos.x - p.x) < 10 && Math.abs(pos.y - p.y) < 10);
    }
    return false;
  };

  const distToSegment = (p, v, w) => {
    const l2 = Math.pow(w.x - v.x, 2) + Math.pow(w.y - v.y, 2);
    if (l2 === 0) return Math.sqrt(Math.pow(p.x - v.x, 2) + Math.pow(p.y - v.y, 2));
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.sqrt(Math.pow(p.x - (v.x + t * (w.x - v.x)), 2) + Math.pow(p.y - (v.y + t * (w.y - v.y)), 2));
  };

  const handleCanvasMouseMove = (e) => {
    const pos = getMousePos(e);

    if (dragging) {
      setAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== dragging.id) return a;
          const newX = pos.x - dragging.offsetX;
          const newY = pos.y - dragging.offsetY;
          if (a.type === 'line') {
            const dx = newX - a.x;
            const dy = newY - a.y;
            return { ...a, x: newX, y: newY, x2: a.x2 + dx, y2: a.y2 + dy };
          }
          return { ...a, x: newX, y: newY };
        }),
      );
      return;
    }

    if (isDrawing && activeTool === TOOLS.FREEHAND && currentFreehand) {
      setCurrentFreehand((prev) => ({ ...prev, points: [...prev.points, pos] }));
      return;
    }

    if (isDrawing && drawStart) {
      // Live preview handled via canvas overlay
      drawShapePreview(pos);
    }
  };

  const handleCanvasMouseUp = (e) => {
    if (dragging) {
      setDragging(null);
      return;
    }

    const pos = getMousePos(e);

    if (isDrawing && activeTool === TOOLS.FREEHAND && currentFreehand) {
      setAnnotations((prev) => [...prev, { ...currentFreehand, points: [...currentFreehand.points, pos] }]);
      setCurrentFreehand(null);
      setIsDrawing(false);
      return;
    }

    if (isDrawing && drawStart) {
      const x = Math.min(drawStart.x, pos.x);
      const y = Math.min(drawStart.y, pos.y);
      const width = Math.abs(pos.x - drawStart.x);
      const height = Math.abs(pos.y - drawStart.y);

      if (width < 5 && height < 5 && activeTool !== TOOLS.LINE) {
        setIsDrawing(false);
        setDrawStart(null);
        clearPreviewCanvas();
        return;
      }

      let annotation;
      if (activeTool === TOOLS.RECT) {
        annotation = {
          id: Date.now() + Math.random(),
          type: 'rect',
          page: currentPage,
          applyMode,
          x,
          y,
          width,
          height,
          strokeColor,
          fillColor: 'transparent',
          strokeWidth: shapeStrokeWidth,
        };
      } else if (activeTool === TOOLS.CIRCLE) {
        annotation = {
          id: Date.now() + Math.random(),
          type: 'circle',
          page: currentPage,
          applyMode,
          x,
          y,
          width,
          height,
          strokeColor,
          fillColor: 'transparent',
          strokeWidth: shapeStrokeWidth,
        };
      } else if (activeTool === TOOLS.LINE) {
        annotation = {
          id: Date.now() + Math.random(),
          type: 'line',
          page: currentPage,
          applyMode,
          x: drawStart.x,
          y: drawStart.y,
          x2: pos.x,
          y2: pos.y,
          strokeColor,
          strokeWidth: shapeStrokeWidth,
        };
      }

      if (annotation) {
        setAnnotations((prev) => [...prev, annotation]);
        setSelectedAnnotation(annotation.id);
      }
      setIsDrawing(false);
      setDrawStart(null);
      clearPreviewCanvas();
    }
  };

  const drawShapePreview = (pos) => {
    const canvas = canvasRef.current;
    if (!canvas || !drawStart) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = shapeStrokeWidth;

    if (activeTool === TOOLS.RECT) {
      const x = Math.min(drawStart.x, pos.x);
      const y = Math.min(drawStart.y, pos.y);
      ctx.strokeRect(x, y, Math.abs(pos.x - drawStart.x), Math.abs(pos.y - drawStart.y));
    } else if (activeTool === TOOLS.CIRCLE) {
      const x = Math.min(drawStart.x, pos.x);
      const y = Math.min(drawStart.y, pos.y);
      const w = Math.abs(pos.x - drawStart.x);
      const h = Math.abs(pos.y - drawStart.y);
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (activeTool === TOOLS.LINE) {
      ctx.beginPath();
      ctx.moveTo(drawStart.x, drawStart.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
  };

  const clearPreviewCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  // Draw freehand in real-time on the overlay canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (currentFreehand && currentFreehand.points.length > 1) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = currentFreehand.color;
      ctx.lineWidth = currentFreehand.brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(currentFreehand.points[0].x, currentFreehand.points[0].y);
      for (let i = 1; i < currentFreehand.points.length; i++) {
        ctx.lineTo(currentFreehand.points[i].x, currentFreehand.points[i].y);
      }
      ctx.stroke();
    }
  }, [currentFreehand]);

  const updateAnnotation = (id, updates) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  };

  const deleteAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedAnnotation === id) setSelectedAnnotation(null);
    if (editingTextId === id) setEditingTextId(null);
  };

  const currentPageAnnotations = annotations.filter((a) => a.page === currentPage);
  const selectedData = annotations.find((a) => a.id === selectedAnnotation);

  // Determine which pages an annotation should be applied to
  const getTargetPages = (ann, totalPageCount) => {
    switch (ann.applyMode || 'current') {
      case 'all':
        return Array.from({ length: totalPageCount }, (_, i) => i + 1);
      case 'even':
        return Array.from({ length: totalPageCount }, (_, i) => i + 1).filter((p) => p % 2 === 0);
      case 'odd':
        return Array.from({ length: totalPageCount }, (_, i) => i + 1).filter((p) => p % 2 !== 0);
      case 'current':
      default:
        return [ann.page];
    }
  };

  // Save annotated PDF
  const savePdf = async () => {
    if (!pdfFile) return;
    setSubmitValue(<Spinner />);
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const zoomFactor = zoom / 100;

      for (const ann of annotations) {
        const targetPages = getTargetPages(ann, pages.length);
        const s = scale * zoomFactor;

        for (const pageNum of targetPages) {
          const page = pages[pageNum - 1];
          if (!page) continue;
          const { height: pageHeight } = page.getSize();

          if (ann.type === 'text') {
            const c = hexToRgb(ann.color);
            page.drawText(ann.text, {
              x: ann.x / s,
              y: pageHeight - ann.y / s,
              size: ann.fontSize / s,
              color: rgb(c.r, c.g, c.b),
            });
          } else if (ann.type === 'image') {
            const base64Data = ann.src.split(',')[1];
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
            const image = ann.file.type.includes('png') ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
            page.drawImage(image, {
              x: ann.x / s,
              y: pageHeight - ann.y / s - ann.height / s,
              width: ann.width / s,
              height: ann.height / s,
              opacity: (ann.opacity || 100) / 100,
            });
          } else if (ann.type === 'rect') {
            const c = hexToRgb(ann.strokeColor);
            page.drawRectangle({
              x: ann.x / s,
              y: pageHeight - ann.y / s - ann.height / s,
              width: ann.width / s,
              height: ann.height / s,
              borderColor: rgb(c.r, c.g, c.b),
              borderWidth: ann.strokeWidth / s,
              color:
                ann.fillColor === 'transparent'
                  ? undefined
                  : (() => {
                      const fc = hexToRgb(ann.fillColor);
                      return rgb(fc.r, fc.g, fc.b);
                    })(),
              opacity: ann.fillColor === 'transparent' ? 0 : 1,
            });
          } else if (ann.type === 'circle') {
            const c = hexToRgb(ann.strokeColor);
            const cx = (ann.x + ann.width / 2) / s;
            const cy = pageHeight - (ann.y + ann.height / 2) / s;
            page.drawEllipse({
              x: cx,
              y: cy,
              xScale: ann.width / 2 / s,
              yScale: ann.height / 2 / s,
              borderColor: rgb(c.r, c.g, c.b),
              borderWidth: ann.strokeWidth / s,
              color:
                ann.fillColor === 'transparent'
                  ? undefined
                  : (() => {
                      const fc = hexToRgb(ann.fillColor);
                      return rgb(fc.r, fc.g, fc.b);
                    })(),
              opacity: ann.fillColor === 'transparent' ? 0 : 1,
            });
          } else if (ann.type === 'line') {
            const c = hexToRgb(ann.strokeColor);
            page.drawLine({
              start: { x: ann.x / s, y: pageHeight - ann.y / s },
              end: { x: ann.x2 / s, y: pageHeight - ann.y2 / s },
              thickness: ann.strokeWidth / s,
              color: rgb(c.r, c.g, c.b),
            });
          } else if (ann.type === 'freehand' && ann.points.length > 1) {
            const c = hexToRgb(ann.color);
            for (let i = 0; i < ann.points.length - 1; i++) {
              page.drawLine({
                start: { x: ann.points[i].x / s, y: pageHeight - ann.points[i].y / s },
                end: { x: ann.points[i + 1].x / s, y: pageHeight - ann.points[i + 1].y / s },
                thickness: ann.brushSize / s,
                color: rgb(c.r, c.g, c.b),
              });
            }
          }
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: constants.pdfFileType });
      setResultBlob(blob);
      setSubmitValue('Save PDF');
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
      setStep(3);
    } catch (err) {
      console.error('Error saving annotated PDF:', err);
      setNotify('Failed to save. Please try again.');
      setError('error');
      setSubmitValue('Save PDF');
    }
  };

  const downloadResult = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `annotated_${pdfFile.name}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toolButtons = [
    {
      tool: TOOLS.SELECT,
      label: 'Select',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 2l12 11.2-5.8.5 3.3 7.3-2.2 1-3.2-7.4L7 18.5V2z" />
        </svg>
      ),
    },
    {
      tool: TOOLS.TEXT,
      label: 'Text',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
        </svg>
      ),
    },
    {
      tool: TOOLS.IMAGE,
      label: 'Image',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        </svg>
      ),
    },
    {
      tool: TOOLS.RECT,
      label: 'Rectangle',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      ),
    },
    {
      tool: TOOLS.CIRCLE,
      label: 'Circle',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
        </svg>
      ),
    },
    {
      tool: TOOLS.LINE,
      label: 'Line',
      svg: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <line x1="5" y1="19" x2="19" y2="5" />
        </svg>
      ),
    },
    {
      tool: TOOLS.FREEHAND,
      label: 'Draw',
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
        </svg>
      ),
    },
  ];

  const renderAnnotation = (ann) => {
    const isSelected = selectedAnnotation === ann.id;
    const outline = isSelected ? '2px solid #FF9900' : 'none';

    if (ann.type === 'text') {
      if (editingTextId === ann.id) {
        return (
          <foreignObject
            key={ann.id}
            x={ann.x}
            y={ann.y - ann.fontSize * 1.2}
            width={Math.max(200, ann.text.length * ann.fontSize * 0.7)}
            height={ann.fontSize * 2}
          >
            <input
              autoFocus
              value={ann.text}
              onChange={(e) => updateAnnotation(ann.id, { text: e.target.value })}
              onBlur={() => setEditingTextId(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setEditingTextId(null);
              }}
              style={{
                width: '100%',
                fontSize: `${ann.fontSize}px`,
                fontFamily: ann.fontFamily,
                color: ann.color,
                background: 'rgba(255,255,255,0.8)',
                border: '1px dashed #FF9900',
                outline: 'none',
                padding: '2px 4px',
              }}
            />
          </foreignObject>
        );
      }
      return (
        <text
          key={ann.id}
          x={ann.x}
          y={ann.y}
          fontSize={ann.fontSize}
          fontFamily={ann.fontFamily}
          fill={ann.color}
          style={{ cursor: 'move', outline, userSelect: 'none' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.x, offsetY: pos.y - ann.y });
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditingTextId(ann.id);
          }}
        >
          {ann.text}
        </text>
      );
    }

    if (ann.type === 'image') {
      return (
        <image
          key={ann.id}
          href={ann.src}
          x={ann.x}
          y={ann.y}
          width={ann.width}
          height={ann.height}
          opacity={(ann.opacity || 100) / 100}
          style={{ cursor: 'move', outline }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.x, offsetY: pos.y - ann.y });
          }}
        />
      );
    }

    if (ann.type === 'rect') {
      return (
        <rect
          key={ann.id}
          x={ann.x}
          y={ann.y}
          width={ann.width}
          height={ann.height}
          stroke={ann.strokeColor}
          strokeWidth={ann.strokeWidth}
          fill={ann.fillColor}
          style={{ cursor: 'move' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.x, offsetY: pos.y - ann.y });
          }}
        />
      );
    }

    if (ann.type === 'circle') {
      return (
        <ellipse
          key={ann.id}
          cx={ann.x + ann.width / 2}
          cy={ann.y + ann.height / 2}
          rx={Math.abs(ann.width) / 2}
          ry={Math.abs(ann.height) / 2}
          stroke={ann.strokeColor}
          strokeWidth={ann.strokeWidth}
          fill={ann.fillColor}
          style={{ cursor: 'move' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.x, offsetY: pos.y - ann.y });
          }}
        />
      );
    }

    if (ann.type === 'line') {
      return (
        <line
          key={ann.id}
          x1={ann.x}
          y1={ann.y}
          x2={ann.x2}
          y2={ann.y2}
          stroke={ann.strokeColor}
          strokeWidth={ann.strokeWidth}
          style={{ cursor: 'move' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.x, offsetY: pos.y - ann.y });
          }}
        />
      );
    }

    if (ann.type === 'freehand' && ann.points.length > 1) {
      const d = ann.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
      return (
        <path
          key={ann.id}
          d={d}
          stroke={ann.color}
          strokeWidth={ann.brushSize}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ cursor: 'move' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setSelectedAnnotation(ann.id);
            const pos = getMousePos(e.nativeEvent);
            setDragging({ id: ann.id, offsetX: pos.x - ann.points[0].x, offsetY: pos.y - ann.points[0].y });
          }}
        />
      );
    }

    return null;
  };

  const renderPropertiesPanel = () => {
    if (!selectedData) {
      return (
        <div style={{ color: '#565959', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
          Select an annotation to edit its properties
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#232F3E',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            paddingBottom: '6px',
            borderBottom: '2px solid #FF9900',
          }}
        >
          {selectedData.type} Properties
        </div>

        {/* Apply mode - available for all annotation types */}
        <label style={{ fontSize: '11px', fontWeight: '600', color: '#232F3E' }}>
          Apply To
          <select
            value={selectedData.applyMode || 'current'}
            onChange={(e) => updateAnnotation(selectedData.id, { applyMode: e.target.value })}
            style={{
              width: '100%',
              padding: '6px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              marginTop: '2px',
              fontSize: '12px',
              backgroundColor: '#fff',
            }}
          >
            <option value="current">Current Page Only</option>
            <option value="all">All Pages</option>
            <option value="odd">Odd Pages</option>
            <option value="even">Even Pages</option>
          </select>
        </label>
        {selectedData.applyMode && selectedData.applyMode !== 'current' && (
          <div
            style={{
              fontSize: '10px',
              color: '#FF9900',
              fontWeight: '500',
              padding: '4px 6px',
              backgroundColor: '#FFF8E7',
              borderRadius: '4px',
              border: '1px solid #FFE4B5',
            }}
          >
            ⚡ Will apply to {selectedData.applyMode === 'all' ? 'all' : selectedData.applyMode} pages on save
          </div>
        )}

        {selectedData.type === 'text' && (
          <>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Font Size
              <input
                type="number"
                min="8"
                max="120"
                value={selectedData.fontSize}
                onChange={(e) => updateAnnotation(selectedData.id, { fontSize: parseInt(e.target.value) || 16 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Font
              <select
                value={selectedData.fontFamily}
                onChange={(e) => updateAnnotation(selectedData.id, { fontFamily: e.target.value })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              >
                {FONTS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Color
              <input
                type="color"
                value={selectedData.color}
                onChange={(e) => updateAnnotation(selectedData.id, { color: e.target.value })}
                style={{ width: '100%', height: '30px', border: 'none', cursor: 'pointer', marginTop: '2px' }}
              />
            </label>
          </>
        )}

        {selectedData.type === 'image' && (
          <>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Width
              <input
                type="number"
                min="10"
                value={selectedData.width}
                onChange={(e) => updateAnnotation(selectedData.id, { width: parseInt(e.target.value) || 50 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Height
              <input
                type="number"
                min="10"
                value={selectedData.height}
                onChange={(e) => updateAnnotation(selectedData.id, { height: parseInt(e.target.value) || 50 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Opacity
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={selectedData.opacity || 100}
                  onChange={(e) => updateAnnotation(selectedData.id, { opacity: parseInt(e.target.value) })}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', minWidth: '30px' }}>{selectedData.opacity || 100}%</span>
              </div>
            </label>
          </>
        )}

        {(selectedData.type === 'rect' || selectedData.type === 'circle') && (
          <>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Stroke Color
              <input
                type="color"
                value={selectedData.strokeColor}
                onChange={(e) => updateAnnotation(selectedData.id, { strokeColor: e.target.value })}
                style={{ width: '100%', height: '30px', border: 'none', cursor: 'pointer', marginTop: '2px' }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Fill Color
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={selectedData.fillColor === 'transparent' ? '#ffffff' : selectedData.fillColor}
                  onChange={(e) => updateAnnotation(selectedData.id, { fillColor: e.target.value })}
                  style={{ width: '30px', height: '30px', border: 'none', cursor: 'pointer' }}
                  disabled={selectedData.fillColor === 'transparent'}
                />
                <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={selectedData.fillColor === 'transparent'}
                    onChange={(e) =>
                      updateAnnotation(selectedData.id, { fillColor: e.target.checked ? 'transparent' : '#ffffff' })
                    }
                  />
                  No fill
                </label>
              </div>
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Stroke Width
              <input
                type="number"
                min="1"
                max="20"
                value={selectedData.strokeWidth}
                onChange={(e) => updateAnnotation(selectedData.id, { strokeWidth: parseInt(e.target.value) || 2 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Width
              <input
                type="number"
                min="5"
                value={Math.round(selectedData.width)}
                onChange={(e) => updateAnnotation(selectedData.id, { width: parseInt(e.target.value) || 20 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Height
              <input
                type="number"
                min="5"
                value={Math.round(selectedData.height)}
                onChange={(e) => updateAnnotation(selectedData.id, { height: parseInt(e.target.value) || 20 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
          </>
        )}

        {selectedData.type === 'line' && (
          <>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Color
              <input
                type="color"
                value={selectedData.strokeColor}
                onChange={(e) => updateAnnotation(selectedData.id, { strokeColor: e.target.value })}
                style={{ width: '100%', height: '30px', border: 'none', cursor: 'pointer', marginTop: '2px' }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Thickness
              <input
                type="number"
                min="1"
                max="20"
                value={selectedData.strokeWidth}
                onChange={(e) => updateAnnotation(selectedData.id, { strokeWidth: parseInt(e.target.value) || 2 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
          </>
        )}

        {selectedData.type === 'freehand' && (
          <>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Color
              <input
                type="color"
                value={selectedData.color}
                onChange={(e) => updateAnnotation(selectedData.id, { color: e.target.value })}
                style={{ width: '100%', height: '30px', border: 'none', cursor: 'pointer', marginTop: '2px' }}
              />
            </label>
            <label style={{ fontSize: '11px', fontWeight: '500' }}>
              Brush Size
              <input
                type="number"
                min="1"
                max="20"
                value={selectedData.brushSize}
                onChange={(e) => updateAnnotation(selectedData.id, { brushSize: parseInt(e.target.value) || 3 })}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '2px',
                }}
              />
            </label>
          </>
        )}

        <button
          onClick={() => deleteAnnotation(selectedData.id)}
          style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: 'linear-gradient(to bottom, #ef4444, #dc2626)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
          }}
        >
          🗑️ Delete
        </button>
      </div>
    );
  };

  const cursorStyle =
    activeTool === TOOLS.SELECT
      ? 'default'
      : activeTool === TOOLS.TEXT
        ? 'text'
        : activeTool === TOOLS.FREEHAND
          ? 'crosshair'
          : 'crosshair';

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9900">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        </div>
        <h3 className="service-card-title">PDF Annotator</h3>
        <p className="service-card-desc">Add text, images, shapes & freehand drawings to PDFs</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {step === 2 && annotations.length > 0 && (
                <Button variant="primary" onClick={savePdf}>
                  {submitValue}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="PDF ANNOTATOR — Add text, images, shapes & drawings"
      >
        <div>
          {/* Progress Steps */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '16px',
              padding: '12px 16px',
              background: 'linear-gradient(to right, #232F3E, #37475A)',
              borderRadius: '8px',
            }}
          >
            {['Upload PDF', 'Annotate', 'Download'].map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background:
                      step > i + 1
                        ? '#52c41a'
                        : step === i + 1
                          ? 'linear-gradient(to bottom, #FF9900, #e88b00)'
                          : 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: step === i + 1 ? '2px solid #FEBD69' : 'none',
                  }}
                >
                  {i + 1}
                </div>
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: step === i + 1 ? '600' : '400',
                    color: step >= i + 1 ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}
                >
                  {label}
                </span>
                {i < 2 && <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 8px' }}>→</span>}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div
              className="bordered-box"
              style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', padding: '40px' }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #FFF8E7, #FFE4B5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '32px',
                }}
              >
                📄
              </div>
              <h3 style={{ marginBottom: '8px', color: '#232F3E' }}>Upload a PDF to annotate</h3>
              <p style={{ color: '#565959', marginBottom: '20px' }}>
                Add text, images, shapes, and freehand drawings to your PDF pages.
              </p>
              <FormField errorText={notify}>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfChange}
                  style={{ margin: '0 auto' }}
                />
              </FormField>
            </div>
          )}

          {step === 2 && pagePreview && (
            <div style={{ display: 'flex', gap: '16px' }}>
              {/* Left: Toolbar */}
              <div style={{ width: '200px', flexShrink: 0 }}>
                <div className="bordered-box">
                  <h6 className="container-header">🛠️ Tools</h6>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {toolButtons.map((tb) => (
                      <button
                        key={tb.tool}
                        onClick={() => {
                          if (tb.tool === TOOLS.IMAGE) {
                            imageInputRef.current?.click();
                          } else {
                            setActiveTool(tb.tool);
                          }
                        }}
                        style={{
                          padding: '10px 4px',
                          border: activeTool === tb.tool ? '2px solid #FF9900' : '1px solid #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: activeTool === tb.tool ? '#FFF8E7' : '#fff',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: activeTool === tb.tool ? '600' : '400',
                          color: activeTool === tb.tool ? '#232F3E' : '#565959',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <span style={{ color: activeTool === tb.tool ? '#FF9900' : '#232F3E' }}>{tb.svg}</span>
                        {tb.label}
                      </button>
                    ))}
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Default settings for new annotations */}
                <div className="bordered-box">
                  <h6 className="container-header">🎨 Defaults</h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#232F3E' }}>
                      Apply To
                      <select
                        value={applyMode}
                        onChange={(e) => setApplyMode(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          marginTop: '2px',
                          fontSize: '12px',
                          backgroundColor: '#fff',
                        }}
                      >
                        <option value="current">Current Page</option>
                        <option value="all">All Pages</option>
                        <option value="odd">Odd Pages</option>
                        <option value="even">Even Pages</option>
                      </select>
                    </label>
                    <label style={{ fontSize: '11px', fontWeight: '500' }}>
                      Color
                      <input
                        type="color"
                        value={strokeColor}
                        onChange={(e) => setStrokeColor(e.target.value)}
                        style={{ width: '100%', height: '28px', border: 'none', cursor: 'pointer', marginTop: '2px' }}
                      />
                    </label>
                    {activeTool === TOOLS.TEXT && (
                      <>
                        <label style={{ fontSize: '11px', fontWeight: '500' }}>
                          Font Size
                          <input
                            type="number"
                            min="8"
                            max="120"
                            value={fontSize}
                            onChange={(e) => setFontSize(parseInt(e.target.value) || 16)}
                            style={{
                              width: '100%',
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              marginTop: '2px',
                            }}
                          />
                        </label>
                        <label style={{ fontSize: '11px', fontWeight: '500' }}>
                          Font
                          <select
                            value={fontFamily}
                            onChange={(e) => setFontFamily(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              marginTop: '2px',
                            }}
                          >
                            {FONTS.map((f) => (
                              <option key={f} value={f}>
                                {f}
                              </option>
                            ))}
                          </select>
                        </label>
                      </>
                    )}
                    {activeTool === TOOLS.FREEHAND && (
                      <label style={{ fontSize: '11px', fontWeight: '500' }}>
                        Brush Size
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={brushSize}
                          onChange={(e) => setBrushSize(parseInt(e.target.value) || 3)}
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            marginTop: '2px',
                          }}
                        />
                      </label>
                    )}
                    {(activeTool === TOOLS.RECT || activeTool === TOOLS.CIRCLE || activeTool === TOOLS.LINE) && (
                      <label style={{ fontSize: '11px', fontWeight: '500' }}>
                        Stroke Width
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={shapeStrokeWidth}
                          onChange={(e) => setShapeStrokeWidth(parseInt(e.target.value) || 2)}
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            marginTop: '2px',
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Center: PDF Canvas */}
              <div style={{ flex: 1 }}>
                <div className="bordered-box">
                  {/* Page nav + zoom */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                      padding: '10px 14px',
                      background: 'linear-gradient(to right, #232F3E, #37475A)',
                      borderRadius: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        ◀
                      </button>
                      <span style={{ fontSize: '13px', color: '#fff', fontWeight: '500' }}>
                        Page {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        ▶
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => handleZoom(-10)}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        −
                      </button>
                      <span
                        style={{
                          fontSize: '13px',
                          minWidth: '40px',
                          textAlign: 'center',
                          color: '#FF9900',
                          fontWeight: '600',
                        }}
                      >
                        {zoom}%
                      </span>
                      <button
                        onClick={() => handleZoom(10)}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={{ overflow: 'auto', maxHeight: '550px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <div
                      ref={containerRef}
                      style={{
                        position: 'relative',
                        width: pageDimensions.width,
                        height: pageDimensions.height,
                        margin: '0 auto',
                        cursor: cursorStyle,
                      }}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={() => {
                        setDragging(null);
                      }}
                    >
                      {/* PDF background */}
                      <img
                        src={pagePreview}
                        alt=""
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                        }}
                      />

                      {/* SVG annotation layer */}
                      <svg
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                        }}
                      >
                        <g style={{ pointerEvents: 'all' }}>{currentPageAnnotations.map(renderAnnotation)}</g>
                      </svg>

                      {/* Canvas overlay for shape preview & freehand */}
                      <canvas
                        ref={canvasRef}
                        width={pageDimensions.width}
                        height={pageDimensions.height}
                        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Properties */}
              <div style={{ width: '200px', flexShrink: 0 }}>
                <div className="bordered-box">
                  <h6 className="container-header">⚙️ Properties</h6>
                  {renderPropertiesPanel()}
                </div>

                {/* Annotation list for current page */}
                <div className="bordered-box">
                  <h6 className="container-header">📋 Annotations ({currentPageAnnotations.length})</h6>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {currentPageAnnotations.length === 0 ? (
                      <div style={{ color: '#565959', fontSize: '12px', textAlign: 'center', padding: '10px' }}>
                        No annotations on this page
                      </div>
                    ) : (
                      currentPageAnnotations.map((ann) => (
                        <div
                          key={ann.id}
                          onClick={() => setSelectedAnnotation(ann.id)}
                          style={{
                            padding: '8px 10px',
                            marginBottom: '4px',
                            borderRadius: '6px',
                            backgroundColor: selectedAnnotation === ann.id ? '#FFF8E7' : '#fff',
                            border: selectedAnnotation === ann.id ? '1px solid #FF9900' : '1px solid #e0e0e0',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.2s ease',
                            color: '#232F3E',
                          }}
                        >
                          <span
                            style={{
                              textTransform: 'capitalize',
                              fontWeight: selectedAnnotation === ann.id ? '600' : '400',
                            }}
                          >
                            {ann.type === 'text'
                              ? `"${ann.text.substring(0, 12)}${ann.text.length > 12 ? '...' : ''}"`
                              : ann.type}
                            {ann.applyMode && ann.applyMode !== 'current' && (
                              <span style={{ fontSize: '9px', color: '#FF9900', marginLeft: '4px', fontWeight: '600' }}>
                                ({ann.applyMode === 'all' ? '📄ALL' : ann.applyMode === 'odd' ? '📄ODD' : '📄EVEN'})
                              </span>
                            )}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAnnotation(ann.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#ef4444',
                              fontSize: '16px',
                              fontWeight: '700',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div
              className="bordered-box"
              style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', padding: '40px' }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #52c41a, #389e0d)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '28px',
                }}
              >
                ✅
              </div>
              <h3 style={{ marginBottom: '8px', color: '#232F3E' }}>PDF Annotated Successfully!</h3>
              <p style={{ color: '#565959', marginBottom: '24px' }}>Your annotated PDF is ready to download.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={downloadResult}>
                  📥 Download PDF
                </Button>
                <Button variant="normal" onClick={() => setStep(2)}>
                  ✏️ Edit Again
                </Button>
                <Button variant="link" onClick={resetState}>
                  🔄 Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PdfAnnotator;
